import { useEffect, useState } from "react";
import { useDropdown, useUser } from "../../actions/authActions";
import { useSelector } from "react-redux";

export default (setSelect, setValue, trigger) => {
  const userData = useSelector((state) => state.app.userData.userData);
  const userId = localStorage.getItem("userId");
  const [isDataPrefilled, setIsDataPrefilled] = useState(false);
  
  // Fetch complete user profile data
  const {
    data: userprofile,
    isLoading: isLoadingProfile,
    isError: isProfileError,
    error: profileError,
  } = useUser(userId);
  
  const { data: dropdowns, isLoading, isError, error } = useDropdown();
  

  useEffect(() => {
    // Prevent multiple prefills
    if (isDataPrefilled) return;
    
    // Wait until user profile and dropdowns are loaded
    if (isLoadingProfile || !userprofile || !dropdowns?.gender?.length) return;

    // Small delay to ensure all fields are mounted and registered
    setTimeout(() => {
      // ✅ FULL NAME (Combine first_name + last_name from registration)
      if (userprofile.first_name || userprofile.last_name) {
        const fullName = `${userprofile.first_name || ''} ${userprofile.last_name || ''}`.trim();
        setValue("full_name", fullName, { shouldValidate: true });
      }

      // ✅ AGE (Calculate from date_of_birth or use existing age)
      if (userprofile.age) {
        setValue("age", String(userprofile.age), { shouldValidate: true });
      } else if (userprofile.date_of_birth) {
        const dob = new Date(userprofile.date_of_birth);
        const now = new Date();
        let calcAge = now.getFullYear() - dob.getFullYear();
        const monthDiff = now.getMonth() - dob.getMonth();
        
        // Adjust age if birthday hasn't occurred this year
        if (monthDiff < 0 || (monthDiff === 0 && now.getDate() < dob.getDate())) {
          calcAge--;
        }
        
        setValue("age", String(calcAge), { shouldValidate: true });
      }

      // ✅ GENDER - Prefill with proper ID mapping
      // Try userprofile first, then fallback to userData
      const genderIdToUse = userprofile.gender_id || userData?.user?.gender_id;
      
      if (genderIdToUse && dropdowns?.gender?.length > 0) {
        
        // Find the exact gender option from dropdown to verify it exists
        const genderOption = dropdowns.gender.find(
          g => g.gender_id === genderIdToUse || 
               g.gender_id === String(genderIdToUse) ||
               g.id === genderIdToUse ||
               g.id === String(genderIdToUse)
        );

        if (genderOption) {
          // Use the actual ID from the dropdown option (prefer gender_id over id)
          const genderId = genderOption.gender_id || genderOption.id;
          const genderIdString = String(genderId);
          
          
          // Update select state
          setSelect((prev) => ({
            ...prev,
            gender: genderIdString,
          }));
          
          // Update form value with proper options
          setValue("gender", genderIdString, { 
            shouldValidate: true,
            shouldDirty: true,
            shouldTouch: true
          });
          
          // Trigger validation if trigger function is available
          if (trigger) {
            trigger("gender");
          }
          
          
        } else {
          console.warn("⚠️ Gender option not found in dropdown. User gender_id:", genderIdToUse);
          console.warn("⚠️ Available gender options:", dropdowns.gender);
        }
      } else {
        console.warn("⚠️ No gender_id found in userprofile or userData");
      }

      // 📅 DATE OF BIRTH (Store for reference)
      if (userprofile.date_of_birth) {
        setValue("date_of_birth", userprofile.date_of_birth);
        
        // Also set as Date object if your form needs it
        const parsedDate = new Date(userprofile.date_of_birth);
        if (!isNaN(parsedDate)) {
          setValue("dob", parsedDate);
        }
        
      }

      // 📧 EMAIL (if available in profile)
      if (userprofile.email) {
        setValue("email", userprofile.email);
      }

      // 📱 MOBILE NUMBER (if available in profile)
      if (userprofile.mobile_num) {
        setValue("mobile_num", userprofile.mobile_num);
      }

      // 🌍 LOCATION - Country, State, City
      if (userprofile.country_id) {
        const countryId = String(userprofile.country_id);
        setValue("country_id", countryId);
        setSelect((prev) => ({
          ...prev,
          country: countryId,
        }));
      }

      if (userprofile.state_id) {
        const stateId = String(userprofile.state_id);
        setValue("state_id", stateId);
        setSelect((prev) => ({
          ...prev,
          state: stateId,
        }));
      }

      if (userprofile.city_id) {
        const cityId = String(userprofile.city_id);
        setValue("city_id", cityId);
        setSelect((prev) => ({
          ...prev,
          city: cityId,
        }));
      }

      // 📸 PROFILE IMAGE (if available)
      if (userprofile.image) {
        setValue("image", userprofile.image);
      }

      // 📞 COUNTRY CODE (for phone)
      if (userprofile.country_code) {
        setValue("country_code", userprofile.country_code);
      }

      // 🔗 REFERRAL CODE (if available)
      if (userprofile.referral_code) {
        setValue("referral_code", userprofile.referral_code);
      }

      // 🎯 ETHNICITY (if already set in profile)
      if (userprofile.ethnicity_id && dropdowns?.ethnicity?.length > 0) {
        const ethnicityId = String(userprofile.ethnicity_id);
        setValue("ethnicity", ethnicityId);
        setSelect((prev) => ({
          ...prev,
          ethnicity: ethnicityId,
        }));
      }

      setIsDataPrefilled(true);
    }, 300); // Small delay to ensure fields are registered
    
  }, [userprofile, isLoadingProfile, dropdowns, setValue, setSelect, isDataPrefilled, userData, trigger]);

  const handleDropdwonSelect = (name, value) => {
    
    setSelect((prev) => ({
      ...prev,
      [name]: value,
    }));
    
    // Also update form value
    setValue(name, value, { shouldValidate: true });
  };

  if (isError || isProfileError) {
    return (
      <div className="flex items-center justify-center w-full">
        <p className="text-secondary">
          Error loading data: {error?.message || profileError?.message}
        </p>
      </div>
    );
  }

  return { 
    userData, 
    userprofile,
    dropdowns, 
    handleDropdwonSelect, 
    isLoading: isLoading || isLoadingProfile,
    isLoadingProfile,
    isDataPrefilled
  };
};